import { useCallback } from 'react';
import { atom, useAtomValue } from 'jotai';
import { uiContextAtomDesignBoardsActions } from '@rocco/ui/design-board/components/DesignBoardsActions';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { toast } from 'sonner';
import { useNavigate } from '@remix-run/react';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import type { Pin } from 'generated/graphql';

export type IsInDesignBoardFn = (itemID: number, itemType: string) => boolean;

/**
 * Atom to store the list of pins.
 */
const pinsAtom = atom<Pin[]>([]);

/**
 * Atom with local storage synchronization for pins.
 */
export const pinsAtomWithLocalStorage = atom(
    (get) => get(pinsAtom),
    (get, set, newStr) => {
        set(pinsAtom, newStr as Pin[]);
        localStorage.setItem('pins', JSON.stringify(newStr));
    }
);

/**
 * Custom hook to handle following items to a design board.
 *
 * @returns {Object} An object containing the handleFollow and isInDesignBoard functions.
 */
export const useHandleFollow = () => {
    const designBoardsActions = useAtomValue(uiContextAtomDesignBoardsActions);
    const pins = useAtomValue(pinsAtomWithLocalStorage);
    const navigate = useNavigate();

    /**
     * Handles the action of following an item to a design board.
     *
     * @param {number | number[]} itemId - The ID(s) of the item to follow.
     * @param {string} itemType - The type of the item to follow.
     */
    const handleFollow = useCallback(
        async (itemId: number | number[], itemType: string) => {
            if (designBoardsActions) {
                const itemIds = Array.isArray(itemId) ? itemId : [itemId];
                itemIds.forEach((id) => {
                    designBoardsActions.trackingConfig?.onBeforeFollow?.(id);
                });

                const board =
                    await designBoardsActions.showSelectDesignBoardModal(true);

                if (board) {
                    designBoardsActions.trackingConfig?.onAfterBoardSelect?.(
                        board
                    );

                    const addedCount =
                        await designBoardsActions.addItemsToBoard(
                            board.id,
                            itemType,
                            itemIds
                        );

                    if (addedCount > 0) {
                        itemIds.forEach((id) => {
                            designBoardsActions.trackingConfig?.onAfterFollow?.(
                                id
                            );
                        });

                        if (
                            designBoardsActions.displayMode ===
                            DisplayMode.Desktop
                        ) {
                            toast.success('Saved to Design Board', {
                                action: {
                                    label: 'Dismiss',
                                    onClick: () => toast.dismiss(),
                                },
                                cancel: {
                                    label: (
                                        <span className="flex gap-2 justify-between items-center">
                                            <span>View Design Board</span>
                                            <ArrowRightIcon className="size-4.5" />
                                        </span>
                                    ),
                                    onClick: () => {
                                        navigate(
                                            `/member/design-boards/${board.urlSegment}`
                                        );
                                    },
                                },
                            });
                        } else {
                            toast.success('Saved to Design Board', {
                                action: {
                                    label: 'View',
                                    onClick: () => {
                                        navigate(
                                            `/member/design-boards/${board.urlSegment}`
                                        );
                                    },
                                },
                            });
                        }
                    }
                }
            }
        },
        [designBoardsActions, navigate]
    );

    /**
     * Checks if an item is already in the design board.
     *
     * @param {number} itemId - The ID of the item to check.
     * @param {string} itemType - The type of the item to check.
     * @returns {boolean} True if the item is in the design board, false otherwise.
     */
    const isInDesignBoard = useCallback(
        (itemId: number, itemType: string) => {
            return pins.some((pin: Pin) => {
                if (pin.__typename === 'PhotoPin') {
                    return (
                        pin.LibraryImageID === itemId &&
                        pin.__typename === itemType
                    );
                } else if (pin.__typename === 'MemberUploadPin') {
                    return pin.TypeID === itemId && pin.__typename === itemType;
                } else {
                    return (
                        pin.PageTypeID === itemId && pin.__typename === itemType
                    );
                }
            });
        },
        [pins]
    );

    const onClick = (e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
    };

    return {
        handleFollow,
        isInDesignBoard,
        onClick,
    };
};
